import { IAlertTypes } from "./types/alertsTypes";
import { ITemplate } from "./types/templateTypes";

export interface ColorVariantGroup {
  representativeId: number;
  variantIds: number[];
  colors: { value: number; label: string }[];
}

export const COLOR_VARIANT_GROUPS: ColorVariantGroup[] = [
  {
    representativeId: 20,
    variantIds: [20, 21, 22, 23, 24],
    colors: [
      { value: 20, label: "Blue" },
      { value: 21, label: "Red" },
      { value: 22, label: "Yellow" },
      { value: 23, label: "Green" },
      { value: 24, label: "Light Green" },
    ],
  },
  {
    representativeId: 26,
    variantIds: [26, 27, 28],
    colors: [
      { value: 26, label: "Green" },
      { value: 27, label: "Orange" },
      { value: 28, label: "Red" },
    ],
  },
];

const hiddenVariantIds = new Set(
  COLOR_VARIANT_GROUPS.flatMap((g) =>
    g.variantIds.filter((id) => id !== g.representativeId),
  ),
);

export const isHiddenVariant = (templateId: number): boolean =>
  hiddenVariantIds.has(templateId);

export const getVariantGroup = (
  templateId: number,
): ColorVariantGroup | undefined =>
  COLOR_VARIANT_GROUPS.find((g) => g.variantIds.includes(templateId));

export const isColorVariantSelected = (
  cardTemplateId: number,
  selectedTemplateId: number,
): boolean => {
  const group = getVariantGroup(cardTemplateId);
  if (!group) return false;
  return group.variantIds.includes(selectedTemplateId);
};

export const templateToAlertType = (t: ITemplate): IAlertTypes => ({
  id: t.templateId,
  name: t.name,
  templateReqHeading: !!t.metadata?.templateReqHeading,
  templateReqMessage: !!t.metadata?.templateReqMessage,
  templateReqIcon: !!t.metadata?.templateReqIcon,
  templateReqLogo: !!t.metadata?.templateReqLogo,
  templateReqZipCode: !!t.metadata?.templateReqZipCode,
  maxAlertMessageCharacter: t.metadata?.maxAlertMessageCharacter ?? 0,
  maxAlertTitleCharacter: t.metadata?.maxAlertTitleCharacter ?? 0,
  maxAlertMessageLine: t.metadata?.maxAlertMessageLine ?? 0,
});

export const getDisplayTemplates = (templates: ITemplate[]): ITemplate[] => {
  const byId = new Map(templates.map((t) => [t.templateId, t]));
  const result: ITemplate[] = [];
  const seenGroups = new Set<number>();

  for (const template of templates) {
    const group = getVariantGroup(template.templateId);
    if (!group) {
      result.push(template);
      continue;
    }
    if (seenGroups.has(group.representativeId)) {
      continue;
    }
    seenGroups.add(group.representativeId);

    const preferred =
      byId.get(group.representativeId) ||
      group.variantIds.map((id) => byId.get(id)).find(Boolean);

    if (preferred) {
      result.push(preferred);
    }
  }

  return result;
};

export const getAvailableVariantColors = (
  group: ColorVariantGroup | undefined,
  availableTemplateIds: Map<number, unknown> | Set<number>,
): { value: number; label: string }[] => {
  if (!group) return [];
  const hasId = (id: number) =>
    availableTemplateIds instanceof Map
      ? availableTemplateIds.has(id)
      : availableTemplateIds.has(id);
  return group.colors.filter((color) => hasId(color.value));
};

export const resolveTemplateName = (
  templateId: number,
  templatesById?: Map<number, ITemplate>,
): string => {
  const fromApi = templatesById?.get(templateId);
  if (fromApi?.name) return fromApi.name;

  const group = getVariantGroup(templateId);
  if (group && templatesById) {
    for (const variantId of group.variantIds) {
      const variant = templatesById.get(variantId);
      if (variant?.name) return variant.name;
    }
  }

  return `Template ${templateId}`;
};
